import { forwardRef } from 'react';
import type React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import FormInput, { type FormInputProps } from '../FormInput';
import FormSelect from '../FormSelect';
import RadixFormTimePicker, { type RadixFormTimePickerProps } from './RadixTimePicker';
import RadixDatePicker, { type RadixDatePickerProps } from '../RadixDatePicker';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { gridPlacementClasses } from '@/chassis/core/common-helpers/class-merge';
import type { ComponentPropsWithoutRef } from 'react';

dayjs.extend(customParseFormat);

type I18nLabel = Record<Language, string>;

// ============================================
// Time Types - Internal Types do Backend
// ============================================

// Date (YYYY-MM-DD)
interface FormDateProps extends Omit<RadixDatePickerProps, 'value' | 'onChange' | 'label' | 'name'> {
    name?: string;
    label?: string;
    i18nLabel?: I18nLabel | string;
    value?: string | null;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onValueChange?: (value: string) => void;
    /** Mascara exibida ao usuário (ex.: DD/MM/YYYY) */
    displayFormat?: string;
    /** Formato emitido para o form/state (ex.: YYYY-MM-DD) */
    valueFormat?: string;
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormDate = forwardRef<HTMLDivElement, FormDateProps>(
    (
        {
            name,
            label,
            i18nLabel,
            value,
            onChange,
            onValueChange,
            displayFormat = 'DD/MM/YYYY',
            valueFormat = 'YYYY-MM-DD',
            formInputSize: _formInputSize,
            rounded: _rounded,
            ...pickerProps
        },
        ref,
    ) => {
        const lang = useLanguage();
        const displayLabel = (typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang]) || label;
        const normalizedValue = value ? value.trim().slice(0, 10) : '';
        const parsedValue = normalizedValue
            ? (() => {
                  const strict = dayjs(normalizedValue, valueFormat, true);
                  if (strict.isValid()) return strict.toDate();
                  const fallback = dayjs(normalizedValue);
                  return fallback.isValid() ? fallback.toDate() : null;
              })()
            : null;

        const handleChange = (next?: Date | null) => {
            const formatted = next ? dayjs(next).format(valueFormat) : '';
            onValueChange?.(formatted);
            if (onChange) {
                onChange({ target: { name, value: formatted } } as React.ChangeEvent<HTMLInputElement>);
            }
        };

        return (
            <div ref={ref} className={gridPlacementClasses(pickerProps.className) || undefined}>
                <RadixDatePicker
                    name={name}
                    label={displayLabel}
                    value={parsedValue}
                    onChange={handleChange}
                    displayFormat={displayFormat}
                    renderHiddenInput={false}
                    {...pickerProps}
                />
            </div>
        );
    },
);
FormDate.displayName = 'FormDate';

// DateTime (ISO 8601)
// Mantido comentado: substituído pelo FormDateTime composto em ./DateTimeField
// type FormDateTimeProps = Omit<FormInputProps, 'type'>;
// export const FormDateTime = forwardRef<HTMLInputElement, FormDateTimeProps>(({ formInputSize, rounded, ...props }, ref) => {
//     return <FormInput {...props} ref={ref} type="datetime-local" formInputSize={formInputSize} rounded={rounded} />;
// });
// FormDateTime.displayName = 'FormDateTime';

// Time.Hour (0-23)
interface FormTimeHourProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'min' | 'max' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeHour = forwardRef<HTMLInputElement, FormTimeHourProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={0} max={23} step={1} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '0-23'} />;
});
FormTimeHour.displayName = 'FormTimeHour';

// Time.Minute (0-59)
interface FormTimeMinuteProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'min' | 'max' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeMinute = forwardRef<HTMLInputElement, FormTimeMinuteProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={0} max={59} step={1} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '0-59'} />;
});
FormTimeMinute.displayName = 'FormTimeMinute';

// Time.Second (0-59)
interface FormTimeSecondProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'min' | 'max' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeSecond = forwardRef<HTMLInputElement, FormTimeSecondProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={0} max={59} step={1} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '0-59'} />;
});
FormTimeSecond.displayName = 'FormTimeSecond';

// Time.Millisecond (0-999) - NOVO
interface FormTimeMillisecondProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'min' | 'max' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeMillisecond = forwardRef<HTMLInputElement, FormTimeMillisecondProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={0} max={999} step={1} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '0-999'} />;
});
FormTimeMillisecond.displayName = 'FormTimeMillisecond';

// Time.Day (1-31)
interface FormTimeDayProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'min' | 'max' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeDay = forwardRef<HTMLInputElement, FormTimeDayProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={1} max={31} step={1} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '1-31'} />;
});
FormTimeDay.displayName = 'FormTimeDay';

// Time.Month (1-12 select)
interface FormTimeMonthProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    formSelectSize?: 'sm' | 'lg';
}

export const FormTimeMonth = forwardRef<HTMLSelectElement, FormTimeMonthProps>(({ formSelectSize, ...props }, ref) => {
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

    return (
        <FormSelect {...props} ref={ref} formSelectSize={formSelectSize}>
            <option value="">Selecione o mês...</option>
            {months.map((month, index) => (
                <option key={index + 1} value={index + 1}>
                    {month}
                </option>
            ))}
        </FormSelect>
    );
});
FormTimeMonth.displayName = 'FormTimeMonth';

// Time.Year (YYYY)
interface FormTimeYearProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: any;
}

export const FormTimeYear = forwardRef<HTMLInputElement, FormTimeYearProps>(({ formInputSize, rounded, onChange, max = 9999, min, label, i18nLabel, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
        if (onChange) {
            // Normalize the value on the same event to avoid losing name/type in downstream handlers
            (e.target as HTMLInputElement).value = digits;
            (e.currentTarget as HTMLInputElement).value = digits;
            onChange(e);
        }
    };

    const value = props.value as any;
    const displayValue = value === undefined || value === null ? '' : String(value).replace(/\D/g, '').slice(0, 4);

    return (
        <FormInput
            {...props}
            value={displayValue}
            ref={ref}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={4}
            min={min}
            max={max}
            onChange={handleChange}
            formInputSize={formInputSize}
            rounded={rounded}
            placeholder={props.placeholder || 'YYYY'}
            label={label}
            i18nLabel={i18nLabel}
        />
    );
});
FormTimeYear.displayName = 'FormTimeYear';

// Time.WeekDay (1-7: Monday-Sunday)
interface FormTimeWeekDayProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    formSelectSize?: 'sm' | 'lg';
}

export const FormTimeWeekDay = forwardRef<HTMLSelectElement, FormTimeWeekDayProps>(({ formSelectSize, ...props }, ref) => {
    const weekDays = [
        { value: 1, label: 'Segunda-feira' },
        { value: 2, label: 'Terça-feira' },
        { value: 3, label: 'Quarta-feira' },
        { value: 4, label: 'Quinta-feira' },
        { value: 5, label: 'Sexta-feira' },
        { value: 6, label: 'Sábado' },
        { value: 7, label: 'Domingo' },
    ];

    return (
        <FormSelect {...props} ref={ref} formSelectSize={formSelectSize}>
            <option value="">Selecione o dia...</option>
            {weekDays.map((day) => (
                <option key={day.value} value={day.value}>
                    {day.label}
                </option>
            ))}
        </FormSelect>
    );
});
FormTimeWeekDay.displayName = 'FormTimeWeekDay';

// Time.YearWeek (1-52)
interface FormTimeYearWeekProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'min' | 'max' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeYearWeek = forwardRef<HTMLInputElement, FormTimeYearWeekProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={1} max={52} step={1} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '1-52'} />;
});
FormTimeYearWeek.displayName = 'FormTimeYearWeek';

// Time (base - HH:MM:SS) - NOVO
interface FormTimeProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTime = forwardRef<HTMLInputElement, FormTimeProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="time" step="1" formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'HH:MM:SS'} />;
});
FormTime.displayName = 'FormTime';

// Time.HourAndMinute (HH:MM) - NOVO
interface FormTimeHourAndMinuteProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeHourAndMinute = forwardRef<HTMLInputElement, FormTimeHourAndMinuteProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="time" formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'HH:MM'} />;
});
FormTimeHourAndMinute.displayName = 'FormTimeHourAndMinute';

// Time.WithMillisecond (HH:MM:SS.fff) - NOVO
interface FormTimeWithMillisecondProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
}

export const FormTimeWithMillisecond = forwardRef<HTMLInputElement, FormTimeWithMillisecondProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="time" step="0.001" formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || 'HH:MM:SS.fff'} />;
});
FormTimeWithMillisecond.displayName = 'FormTimeWithMillisecond';

// FormTimePicker (Radix + Popover)
interface FormTimePickerProps extends RadixFormTimePickerProps {}

export const FormTimePicker = forwardRef<HTMLInputElement, FormTimePickerProps>((props, _ref) => {
    return <RadixFormTimePicker {...props} />;
});
FormTimePicker.displayName = 'FormTimePicker';
